import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Ban, Trash2 } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { useBatches, useBatchActions } from '@/hooks/useDispatcher';
import { formatDateTime, truncate } from '@/lib/utils';
import type { Batch } from '@/types/api';

export default function BatchesPage() {
  const navigate = useNavigate();
  const { data, isLoading } = useBatches();
  const { remove, cancel } = useBatchActions();

  function handleCancel(b: Batch, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Annuler le batch "${b.source_batch_id ?? b.id.slice(0, 8)}" ?\nLes jobs queued seront marqués cancelled.`)) {
      return;
    }
    cancel.mutate(b.id, {
      onSuccess: () => toast.success('Batch annulé'),
      onError: () => toast.error('Échec de l\'annulation'),
    });
  }

  function handleRemove(b: Batch, e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm(`Supprimer DÉFINITIVEMENT le batch ?\nCela vire le batch et tous ses jobs/results/states (cascade).`)) {
      return;
    }
    remove.mutate(b.id, {
      onSuccess: () => toast.success('Batch supprimé'),
      onError: () => toast.error('Échec de la suppression'),
    });
  }

  const columns = useMemo<ColumnDef<Batch>[]>(
    () => [
      { accessorKey: 'source', header: 'Source' },
      { accessorKey: 'campaign_name', header: 'Campagne', cell: ({ getValue }) => getValue<string>() ?? '—' },
      {
        accessorKey: 'source_batch_id',
        header: 'Batch externe',
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue<string>()}</span>,
      },
      {
        accessorKey: 'channels_enabled',
        header: 'Channels',
        cell: ({ getValue }) => (
          <div className="flex gap-1">
            {(getValue<string[]>() ?? []).map((c) => (
              <Badge key={c} tone="brand">
                {c}
              </Badge>
            ))}
          </div>
        ),
      },
      { accessorKey: 'jobs_count', header: 'Jobs' },
      { accessorKey: 'contacts_count', header: 'Contacts' },
      {
        accessorKey: 'cache_hit',
        header: 'Cache',
        cell: ({ getValue }) =>
          getValue<boolean>() ? <Badge tone="brand">cache</Badge> : <Badge tone="gray">live</Badge>,
      },
      {
        accessorKey: 'callback_url',
        header: 'Callback',
        cell: ({ getValue }) => (
          <span className="text-xs text-gray-500" title={getValue<string>()}>
            {truncate(getValue<string>() ?? '—', 32)}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ getValue }) => <StatusBadge status={getValue<string>()} />,
      },
      {
        accessorKey: 'created_at',
        header: 'Créé',
        cell: ({ getValue }) => formatDateTime(getValue<string>()),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={(e) => handleCancel(row.original, e)}
              className="text-xs text-amber-600 hover:text-amber-800 inline-flex items-center gap-1"
              title="Annuler le batch (queued/running → cancelled)"
            >
              <Ban size={12} /> Annuler
            </button>
            <button
              type="button"
              onClick={(e) => handleRemove(row.original, e)}
              className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1"
              title="Supprimer définitivement (cascade jobs)"
            >
              <Trash2 size={12} />
            </button>
          </div>
        ),
      },
    ],
    // We intentionally omit handleCancel/handleRemove from deps —
    // mutation refs are stable enough for this click-handler scope.
    [],
  );

  return (
    <div>
      <PageHeader title="Batches" description="Vue par batch source (getfishnet, BIG.call, …)" />
      <DataTable
        data={data}
        columns={columns}
        loading={isLoading}
        searchPlaceholder="Filtrer par campagne, source, batch ID…"
        onRowClick={(row) => navigate(`/batches/${row.id}`)}
        emptyTitle="Aucun batch"
      />
    </div>
  );
}
